// Money is whole cents (RK-SYS-002 conventions). Rand is the only currency.
export function formatRand(cents: number | bigint | null | undefined, opts: { sign?: boolean } = {}): string {
  if (cents === null || cents === undefined) return "—";
  const c = typeof cents === "bigint" ? Number(cents) : cents;
  const neg = c < 0;
  const abs = Math.abs(Math.round(c));
  const rands = Math.floor(abs / 100);
  const rest = String(abs % 100).padStart(2, "0");
  const grouped = rands.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const s = `R${grouped}.${rest}`;
  if (neg) return `-${s}`;
  return opts.sign && c > 0 ? `+${s}` : s;
}

/** Parse "R1,656,000.00", "1656000", "1 656 000,00" into cents. Returns null when unreadable. */
export function parseRandToCents(text: string): number | null {
  const t = text.replace(/[R\s\u00a0]/gi, "").trim();
  if (!t) return null;
  let norm = t;
  if (/,\d{2}$/.test(t) && !/\.\d{2}$/.test(t)) norm = t.replace(/\./g, "").replace(",", ".");
  norm = norm.replace(/,/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(norm)) return null;
  const [whole, frac = ""] = norm.replace("-", "").split(".");
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  return norm.startsWith("-") ? -cents : cents;
}

/** The VAT basis of an amount (ED-RK-023; Roelof's BD-385 Q2): every figure says whether VAT is in it. */
export type VatBasis = "incl" | "excl";

/** The words of a basis, as they stand beside an amount: "incl. VAT" or "excl. VAT". */
export const basisWords = (basis: VatBasis): string => (basis === "incl" ? "incl. VAT" : "excl. VAT");

/** An amount with its basis on the same line: priceWithBasis(4025000, "incl") is "R40,250.00 incl. VAT". */
export function priceWithBasis(cents: number | null | undefined, basis: VatBasis): string {
  return `${formatRand(cents)} ${basisWords(basis)}`;
}

/**
 * The excl.-VAT part of an incl.-VAT amount on one invoice, at that invoice's own excl./incl. ratio from its printed totals, to the cent
 * (BD-385 Q2: money that moves shows incl. VAT first and excl. VAT in small text). There is no fixed rate: an invoice with no VAT gives excl. = incl.,
 * and R546,250.00 on an invoice of R885,000.00 excl. / R1,017,750.00 incl. is R475,000.00 excl. Null when the invoice's totals are not known.
 */
export function exclAtRatio(cents: number, totalExcl: number | null | undefined, totalIncl: number | null | undefined): number | null {
  if (totalExcl == null || totalIncl == null || !Number.isFinite(Number(totalExcl)) || !Number.isFinite(Number(totalIncl))) return null;
  if (Number(totalIncl) === 0) return Number(totalExcl) === 0 ? cents : null;
  return Math.round((cents * Number(totalExcl)) / Number(totalIncl));
}
